"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { usePublicContext } from "../context/PublicContext";
import { Category } from "../types/public.types";

export default function SubcategoryCarousel() {
  const router = useRouter();
  const { categories: rawCategories, loadingCategories: isLoading } = usePublicContext();
  const [categories, setCategories] = useState<Category[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  // Filters only active/valid ones
  useEffect(() => {
    if (rawCategories) {
      setCategories(rawCategories);
    }
  }, [rawCategories]);

  const frameIdRef = useRef<number | null>(null);

  // Update arrow visibility state based on scroll position
  const checkScrollPosition = () => {
    if (frameIdRef.current !== null) return;

    frameIdRef.current = requestAnimationFrame(() => {
      frameIdRef.current = null;
      const container = scrollContainerRef.current;
      if (container) {
        const { scrollLeft, scrollWidth, clientWidth } = container;
        const nextLeft = scrollLeft > 5;
        const nextRight = scrollLeft + clientWidth < scrollWidth - 5;

        setShowLeftArrow((prev) => (prev !== nextLeft ? nextLeft : prev));
        setShowRightArrow((prev) => (prev !== nextRight ? nextRight : prev));
      }
    });
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    let initialTimeoutId: any = null;

    if (container) {
      container.addEventListener("scroll", checkScrollPosition, { passive: true });
      // Run once initially after paint to avoid forced reflow
      initialTimeoutId = setTimeout(() => {
        checkScrollPosition();
      }, 100);
    }

    // Handle container resize updates using ResizeObserver
    let resizeObserver: ResizeObserver | null = null;
    if (container && typeof window !== "undefined" && "ResizeObserver" in window) {
      resizeObserver = new ResizeObserver(() => {
        checkScrollPosition();
      });
      resizeObserver.observe(container);
    } else {
      window.addEventListener("resize", checkScrollPosition, { passive: true });
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", checkScrollPosition);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener("resize", checkScrollPosition);
      }
      if (initialTimeoutId) {
        clearTimeout(initialTimeoutId);
      }
      if (frameIdRef.current !== null) {
        cancelAnimationFrame(frameIdRef.current);
      }
    };
  }, [categories]);

  // Scroll handler for navigation arrows
  const scroll = (direction: "left" | "right") => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = container.clientWidth * 0.75; // Scroll 75% of view width
      container.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  if (isLoading) {
    return (
      <section className="py-10 relative bg-white overflow-hidden border-t border-slate-100 min-h-[500px]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-green-600 tracking-tight">
              Shop By <span className="text-black-600">Categories</span>
            </h2>
            <p className="mt-3 text-sm sm:text-base text-slate-500 font-medium">
              Explore our wide range of agricultural products & solutions
            </p>
            <div className="w-12 h-1 bg-green-600 mx-auto mt-4 rounded-full" />
          </div>

          {/* Carousel Viewport Wrapper Skeleton */}
          <div className="relative -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
            <div className="flex gap-6 overflow-x-hidden pb-8 pt-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="flex-none w-[260px] bg-white rounded-lg border border-slate-200/80 p-4 h-[330px] flex flex-col justify-between animate-pulse"
                >
                  <div className="flex flex-col flex-1">
                    <div className="w-full aspect-[4/3] bg-slate-100 rounded-md" />
                    <div className="flex flex-col items-center mt-3">
                      <div className="h-5 bg-slate-200 rounded-md w-3/4 mb-2" />
                      <div className="h-3 bg-slate-200 rounded-md w-5/6 mb-1.5" />
                      <div className="h-3 bg-slate-200 rounded-md w-2/3" />
                    </div>
                  </div>
                  <div className="mt-3 flex justify-center">
                    <div className="h-4 bg-slate-200 rounded-md w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (categories.length === 0) {
    return (
      <section className="py-10 relative bg-white overflow-hidden border-t border-slate-100 min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-700">More Categories Coming Soon</h2>
          <p className="text-slate-500 mt-2">We are currently updating our catalog.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-5 relative bg-white overflow-hidden border-t border-slate-100 min-h-[500px]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Section Header */}
        <div className="text-center max-w-5xl mx-auto mb-12">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-green-600 tracking-tight mt-3">
            Shop By Categories
          </h2>
          <p className="mt-3 text-base text-slate-500 font-medium max-w-4xl mx-auto">
            Explore agriculture through three connected business areas—Agri-Inputs for farming, Agri-Output for agricultural produce, and Agri-Services for professional, technical and business support.          </p>
          <div className="w-12 h-1.5 bg-green-600 mx-auto mt-4 rounded-full" />
        </div>

        {/* Categories Grid */}
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pb-5 justify-center">
            {categories.map((cat) => {
              const cardUrl = `/${cat.slug}`;

              return (
                <div
                  key={cat._id}
                  className="w-full"
                >
                  <div
                    onClick={() => router.push(cardUrl)}
                    className="group bg-white rounded-sm border border-slate-100 hover:border-green-200 transition-all duration-350 hover:shadow-xl hover:shadow-green-900/5 hover:-translate-y-1.5 flex flex-col justify-between p-5  h-[390px] overflow-hidden text-center cursor-pointer select-none"
                  >

                    <div className="flex flex-col flex-1">
                      {/* Rounded Image Frame */}
                      <div className="relative w-full aspect-[4/3] rounded-sm overflow-hidden bg-slate-50 shadow-inner">
                        {cat.imageUrl ? (
                          <Image
                            src={cat.imageUrl}
                            alt={cat.name}
                            fill
                            sizes="(max-width: 640px) 100vw, 300px"
                            className="object-cover rounded-sm transition-transform duration-700 ease-out group-hover:scale-105"
                          />
                        ) : (
                          /* Fallback Initials Background */
                          <div className="w-full h-full bg-gradient-to-br from-green-50 to-emerald-100/50 flex items-center justify-center rounded-xl">
                            <span className="text-3xl font-black text-green-700/20 tracking-wider">
                              {cat.name.slice(0, 2).toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Content Area */}
                      <div className="flex flex-col items-center mt-4">
                        {/* Category Title */}
                        <h3 className="text-lg font-extrabold text-slate-800 capitalize line-clamp-1 group-hover:text-green-600 transition-colors duration-200 h-[28px] flex items-center justify-center">
                          {cat.name}
                        </h3>

                        {/* Description Wrapper with Fixed Height to align all buttons and text */}
                        <div className="h-[48px] flex items-center justify-center mt-2 px-1 overflow-hidden">
                          <p className="text-slate-500 text-sm font-medium line-clamp-2 leading-relaxed">
                            {cat.description || `Browse quality ${cat.name.toLowerCase()} products and services listed on our platform.`}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Shop Now Action Link */}
                    <div className="mt-auto pt-2">
                      <span
                        className="w-full inline-flex items-center justify-center px-6 py-2.5 text-sm font-bold text-green-700  hover:text-white rounded-xl transition-all duration-300"
                      >
                        Explore Category &rarr;
                      </span>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
